import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, User, Building, Phone } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';

interface QuickCreateClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialName: string;
    onSuccess: (negotiation: { id: number, cliente_nome: string, tipo_cliente: 'PF' | 'PJ' }) => void;
}

const QuickCreateClientModal: React.FC<QuickCreateClientModalProps> = ({ isOpen, onClose, initialName, onSuccess }) => {
    const [name, setName] = useState(initialName);
    const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ'>('PF');
    const [whatsapp, setWhatsapp] = useState('');
    const [loading, setLoading] = useState(false);
    const { addToast } = useToast();



    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Get current user for owner
            const { data: { user } } = await supabase.auth.getUser();

            // Resolve default CRM pipeline and its first stage to avoid orphan records outside CRM views
            const { data: defaultPipeline } = await supabase
                .from('pipelines')
                .select('id')
                .eq('name', 'Funil de Vendas')
                .maybeSingle();

            let defaultStage = 'prospeccao';
            if (defaultPipeline?.id) {
                const { data: firstStage } = await supabase
                    .from('pipeline_stages')
                    .select('name')
                    .eq('pipeline_id', defaultPipeline.id)
                    .order('position', { ascending: true })
                    .limit(1)
                    .maybeSingle();
                if (firstStage?.name) defaultStage = firstStage.name;
            }

            // Create Negotiation
            const { data, error } = await supabase
                .from('negociacoes')
                .insert([{
                    titulo: name,
                    cliente_nome: name,
                    tipo_cliente: tipoCliente,
                    whatsapp: whatsapp,
                    stage: defaultStage,
                    status: 'em_andamento',
                    pipeline_id: defaultPipeline?.id || null,
                    responsavel_id: user?.id,
                    consumo_medio_kwh: 0
                }])
                .select('id, cliente_nome, tipo_cliente')
                .single();

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'criar_cliente_rapido', origem: 'acao' });

            onSuccess(data);
            onClose();
        } catch (error: any) {
            console.error('Error creating client:', error);
            addToast('Erro ao criar cliente. Tente novamente.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className={`fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4 ${!isOpen ? 'hidden' : ''}`}>
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden transform scale-100 transition-all">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-gray-800">Novo Cliente Rápido</h3>
                    <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSave} className="p-5 space-y-4">

                    {/* Client Type */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => setTipoCliente('PF')}
                            className={`flex-1 py-3 px-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${tipoCliente === 'PF'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                                }`}
                        >
                            <User size={20} />
                            <span className="font-bold text-sm">Pessoa Física</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setTipoCliente('PJ')}
                            className={`flex-1 py-3 px-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${tipoCliente === 'PJ'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                                }`}
                        >
                            <Building size={20} />
                            <span className="font-bold text-sm">Empresa</span>
                        </button>
                    </div>

                    {/* Name */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                            {tipoCliente === 'PJ' ? 'Razão Social' : 'Nome do Cliente'}
                        </label>
                        <input
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none"
                            placeholder={tipoCliente === 'PJ' ? 'Razão Social da Empresa' : 'Nome completo'}
                        />
                    </div>

                    {/* WhatsApp */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp</label>
                        <div className="relative">
                            <input
                                type="text"
                                required
                                value={whatsapp}
                                onChange={(e) => {
                                    // Mask (XX) XXXXX-XXXX
                                    let v = e.target.value.replace(/\D/g, '');
                                    if (v.length > 11) v = v.slice(0, 11);
                                    if (v.length > 7) v = v.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3');
                                    else if (v.length > 2) v = v.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
                                    else if (v.length > 0) v = v.replace(/^(\d{0,2})/, '($1');
                                    setWhatsapp(v);
                                }}
                                className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-primary outline-none"
                                placeholder="(11) 99999-9999"
                            />
                            <Phone size={14} className="absolute left-3 top-3 text-gray-400" />
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 font-semibold hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !name || !whatsapp}
                            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? 'Salvando...' : 'Criar Cliente'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default QuickCreateClientModal;
